// #region Imports

/* SignalR */
import { HubConnectionState } from '@microsoft/signalr';

/* Telestream */
import { ConnectionService } from '../services/connection.service';
import { LEVELS, sendEphemeralSnack } from './utilities/snacks.utils';
import {
  SET_LICENSE_ENTITLEMENTS,
  SET_LICENSE_EXPIRATION,
  SET_LICENSE_IS_ACTIVE,
  SET_LICENSE_KEY,
  SET_LICENSE_STATE,
  SET_INSTALLATION_ID,
  SET_WAITING_FOR_LICENSE_ACTIVATION,
  SET_WAITING_FOR_LICENSE_DEACTIVATION
} from '@/utils/mutation-types';
import {
  ACTIVATE_LICENSE,
  ACTIVATE_LICENSE_OFFLINE,
  CHECK_LICENSE,
  DEACTIVATE_LICENSE,
  GENERATE_INSTALLATION_ID
} from '@/utils/action-types';

// #endregion Imports

// #region Constants

const RESULT_SUCCESS_STATE      = 'Success';
const VALID_LICENSE_STATE       = 'Valid';
const VALID_ENTITLEMENT_STATE   = 'Enabled';

const GLIM_STANDARD_ENTITLEMENT = 'glimpse.standard';
const GLIM_PRO_ENTITLEMENT      = 'glimpse.pro';

// #endregion Constants

export const license = {

  // #region State

  state: {
    connection:               ConnectionService.getAutoReconnectConnection('/license'),
    isActive:                 false,
    isWaitingForActivation:   false,
    isWaitingForDeactivation: false,
    state:                    undefined,
    key:                      undefined,
    expiration:               undefined,
    entitlements:             undefined,
    installationId:           undefined
  },

  // #endregion State

  // #region Mutations

  mutations: {

    [SET_LICENSE_EXPIRATION](state, expiration) {
      state.expiration = expiration;
    },

    [SET_LICENSE_ENTITLEMENTS](state, entitlements) {
      state.entitlements = entitlements;
    },

    [SET_LICENSE_IS_ACTIVE](state, isActive) {
      state.isActive = isActive;
    },

    [SET_LICENSE_STATE](state, licenseState) {
      state.state = licenseState;
    },

    [SET_LICENSE_KEY](state, key) {
      state.key = key;
    },

    [SET_INSTALLATION_ID](state, id) {
      state.installationId = id;
    },

    [SET_WAITING_FOR_LICENSE_ACTIVATION](state, isWaiting) {
      state.isWaitingForActivation = isWaiting;
    },

    [SET_WAITING_FOR_LICENSE_DEACTIVATION](state, isWaiting) {
      state.isWaitingForDeactivation = isWaiting;
    }

  },

  // #endregion Mutations

  // #region Actions

  actions: {

    async [CHECK_LICENSE](context) {
      if (context.state.connection.state !== HubConnectionState.Connected) {
        await context.state.connection.start();
        context.state.connection.on(
          'LicenseUpdated', 
          response => parseEntitlement(context, response)
        );
      }

      const entitlement = await context.state.connection.invoke('GetEntitlement');
      parseEntitlement(context, entitlement);
    },

    async [ACTIVATE_LICENSE](context, key) {
      context.commit(SET_WAITING_FOR_LICENSE_ACTIVATION, true);

      const result = await context.state.connection.invoke('Activate', key);
      if (result.activationResult === RESULT_SUCCESS_STATE) {
        return;
      }

      context.commit(SET_WAITING_FOR_LICENSE_ACTIVATION, false);
      sendEphemeralSnack(context, LEVELS.ERROR, 'Unable to activate license!');
    },

    async [ACTIVATE_LICENSE_OFFLINE](context, { key, serverKey }) {
      context.commit(SET_WAITING_FOR_LICENSE_ACTIVATION, true);

      const result = await context.state.connection.invoke('ActivateOffline', key, serverKey);
      if (result.activationResult === RESULT_SUCCESS_STATE) {
        return;
      }
      
      context.commit(SET_WAITING_FOR_LICENSE_ACTIVATION, false);
      sendEphemeralSnack(context, LEVELS.ERROR, 'Unable to activate license!');
    },

    [DEACTIVATE_LICENSE](context) {
      context.commit(SET_WAITING_FOR_LICENSE_DEACTIVATION, true);
      context.state.connection.invoke('Deactivate');
    },

    async [GENERATE_INSTALLATION_ID](context, key) {
      const installationId = await context.state.connection.invoke('GetInstallationId', key);
      context.commit(SET_INSTALLATION_ID, installationId);
    }

  }

  // #endregion Actions

}

// #region Utilities

function parseEntitlement(context, entitlement) {
  const licenseState = entitlement.licenseState;
  const entitlementStates = entitlement.entitlementStates ?? {};

  const licenseIsValid = (
    (licenseState === VALID_LICENSE_STATE) &&
    (
      (entitlementStates[GLIM_PRO_ENTITLEMENT] === VALID_ENTITLEMENT_STATE) ||
      (entitlementStates[GLIM_STANDARD_ENTITLEMENT] === VALID_ENTITLEMENT_STATE)
    )
  );

  context.commit(SET_LICENSE_STATE, entitlement.licenseState);
  context.commit(SET_LICENSE_IS_ACTIVE, licenseIsValid);
  context.commit(SET_LICENSE_KEY, entitlement.entity);
  context.commit(SET_LICENSE_ENTITLEMENTS, parseEntitlements(entitlement.entitlementStates));

  if (entitlement.hasExpiration && entitlement.expiryDate) {
    context.commit(SET_LICENSE_EXPIRATION, new Date(entitlement.expiryDate));
  } else {
    context.commit(SET_LICENSE_EXPIRATION, undefined);
  }

  // Waiting for activation?
  if (context.state.isWaitingForActivation) {
    context.commit(SET_WAITING_FOR_LICENSE_ACTIVATION, false);
    if (!licenseIsValid) {
      sendEphemeralSnack(context, LEVELS.ERROR, `Unable to activate license: ${entitlement.description}`);
    } else {
      sendEphemeralSnack(context, LEVELS.SUCCESS, 'License key activated!');
    }
  }

  // Waiting for deactivation?
  if (context.state.isWaitingForDeactivation) {
    context.commit(SET_WAITING_FOR_LICENSE_DEACTIVATION, false);
    sendEphemeralSnack(context, LEVELS.SUCCESS, 'License key deactivated!');
  }
}

function parseEntitlements(rawEntitlements) {
  if (!rawEntitlements) {
    return undefined;
  }

  const entitlements = [];
  for (var key in rawEntitlements) {
    if (key === 'glimpse.standard') continue;
    if (key === 'glimpse.pro') continue;

    if (rawEntitlements[key] === VALID_ENTITLEMENT_STATE) {
      entitlements.push(key.replace('glimpse.', ''));
    }
  }

  return entitlements.length > 0 ? entitlements : undefined;
}

// #endregion Utilities